import React from "react";
import { Link } from "react-router-dom";
import { Leaf, ArrowRight, MapPin, Sparkle, Plant, Camera, Package, ChatCircleDots, ShieldCheck, Heart } from "@phosphor-icons/react";

const HERO_IMG = "https://images.unsplash.com/photo-1629730598431-56b24d711977?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxjb21tZXJjaWFsJTIwZ3JlZW5ob3VzZSUyMG51cnNlcnklMjB3aG9sZXNhbGV8ZW58MHx8fHwxNzc3MzMwMTIyfDA&ixlib=rb-4.1.0&q=85";
const FARMER_IMG = "https://images.unsplash.com/photo-1758524053056-5a7090d49a69?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHxmYXJtZXIlMjB3b3JraW5nJTIwbnVyc2VyeSUyMHBsYW50c3xlbnwwfHx8fDE3NzczMzAxMjJ8MA&ixlib=rb-4.1.0&q=85";
const LEAF_IMG = "https://images.unsplash.com/photo-1769613637793-1d497f0fedf0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxwbGFudCUyMGxlYWYlMjBkaXNlYXNlJTIwY2xvc2UlMjB1cHxlbnwwfHx8fDE3NzczMzAxMjJ8MA&ixlib=rb-4.1.0&q=85";

// botanical leaf SVG decoration
const Sprig = ({ style, color = "#C5D5B3", size = 140, rotate = 0 }) => (
  <svg width={size} height={size} viewBox="0 0 200 200" fill="none" style={{ transform: `rotate(${rotate}deg)`, ...style }} aria-hidden="true">
    <path d="M100 20 Q 140 60 130 110 Q 120 160 100 180" stroke={color} strokeWidth="3" strokeLinecap="round" fill="none" />
    <ellipse cx="80" cy="55" rx="22" ry="10" transform="rotate(-30 80 55)" fill={color} opacity="0.7" />
    <ellipse cx="135" cy="80" rx="24" ry="11" transform="rotate(30 135 80)" fill={color} opacity="0.85" />
    <ellipse cx="78" cy="115" rx="22" ry="10" transform="rotate(-28 78 115)" fill={color} opacity="0.6" />
    <ellipse cx="128" cy="140" rx="20" ry="9" transform="rotate(34 128 140)" fill={color} opacity="0.75" />
  </svg>
);

const Wave = () => (
  <svg viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ display: "block", width: "100%", height: 80 }} aria-hidden="true">
    <path d="M0,32 C240,80 480,0 720,32 C960,64 1200,16 1440,40 L1440,80 L0,80 Z" fill="var(--surface)" />
  </svg>
);

export default function Landing() {
  return (
    <div className="organic-bg" style={{ minHeight: "100vh" }}>
      {/* Top bar */}
      <header style={{ background: "rgba(250, 247, 239, 0.85)", backdropFilter: "saturate(160%) blur(10px)", position: "sticky", top: 0, zIndex: 40, borderBottom: "1px solid var(--border)" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <Link to="/" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 10 }} data-testid="landing-brand">
            <div style={{ width: 42, height: 42, background: "var(--primary)", borderRadius: 14, display: "grid", placeItems: "center", boxShadow: "var(--shadow-sm)" }}>
              <Leaf size={22} color="white" weight="fill" />
            </div>
            <div>
              <div className="font-display" style={{ fontSize: 22, lineHeight: 1 }}>NurserySense</div>
              <div style={{ fontSize: 11, color: "var(--muted-fg)", marginTop: 2 }}>Grown in McMinnville, TN</div>
            </div>
          </Link>
          <nav style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Link to="/blog" className="btn btn-ghost" data-testid="landing-blog-btn">Field Notes</Link>
            <Link to="/login" className="btn btn-ghost" data-testid="landing-login-btn">Sign in</Link>
            <Link to="/apply" className="btn btn-primary" data-testid="landing-cta-register">
              Apply now <ArrowRight size={16} weight="bold" />
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section style={{ padding: "84px 0 48px", position: "relative", overflow: "hidden" }}>
        <Sprig style={{ position: "absolute", top: 60, left: -40 }} size={220} rotate={-20} color="var(--leaf-pale)" />
        <Sprig style={{ position: "absolute", bottom: -40, right: -40 }} size={260} rotate={140} color="var(--secondary-soft)" />
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 24px", display: "grid", gridTemplateColumns: "1.05fr 1fr", gap: 56, alignItems: "center", position: "relative" }} className="hero-grid fade-up">
          <div>
            <span className="badge badge-soft" style={{ marginBottom: 24 }}>
              <Sparkle size={14} weight="fill" /> Spring 2026 · 5 Founding Nurseries · hand-picked
            </span>
            <h1 className="font-display" style={{ fontSize: "clamp(48px, 7vw, 84px)", lineHeight: 0.98, letterSpacing: "-0.025em" }}>
              AI tools that<br />
              <span style={{ position: "relative", display: "inline-block" }}>
                <span style={{ position: "relative", zIndex: 2 }}>grow with you</span>
                <svg style={{ position: "absolute", left: -6, right: -6, bottom: -8, width: "calc(100% + 12px)", height: 18, zIndex: 1 }} viewBox="0 0 200 16" preserveAspectRatio="none" aria-hidden="true">
                  <path d="M2 10 Q 50 2 100 8 T 198 10" stroke="var(--secondary)" strokeWidth="4" fill="none" strokeLinecap="round" />
                </svg>
              </span>
              ,<br />not against you.
            </h1>
            <p style={{ fontSize: 19, marginTop: 28, maxWidth: 540, lineHeight: 1.55, color: "var(--text-soft)" }}>
              Snap a photo of a sick leaf and know what's wrong in five seconds. Track every tree, shrub and shrinkage loss without paperwork. Let a friendly chatbot answer your customers at 2 AM and send the leads straight to you.
            </p>
            <div style={{ display: "flex", gap: 12, marginTop: 32, flexWrap: "wrap" }}>
              <Link to="/apply" className="btn btn-primary" data-testid="hero-cta-register" style={{ padding: "16px 28px", fontSize: 16 }}>
                Apply for a founding slot <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/login" className="btn btn-outline" data-testid="hero-cta-demo" style={{ padding: "16px 28px", fontSize: 16 }}>
                Try the demo
              </Link>
            </div>
            <div style={{ marginTop: 28, display: "flex", gap: 22, flexWrap: "wrap", fontSize: 13, color: "var(--muted-fg)" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}><ShieldCheck weight="fill" size={16} color="var(--primary)" /> No credit card</span>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}><Heart weight="fill" size={16} color="var(--secondary)" /> Setup in 20 min</span>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}><MapPin weight="fill" size={16} color="var(--accent)" /> Trained on TN species</span>
            </div>
          </div>

          {/* Hero collage */}
          <div style={{ position: "relative", height: 560 }}>
            <div className="frame" style={{ position: "absolute", top: 20, right: 0, width: "82%", height: 440, transform: "rotate(2deg)" }}>
              <img src={HERO_IMG} alt="Greenhouse" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </div>
            <div className="card-soft" style={{ position: "absolute", bottom: 30, left: 0, padding: 18, maxWidth: 240, transform: "rotate(-3deg)", background: "var(--surface)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: "var(--primary-soft)", display: "grid", placeItems: "center" }}>
                  <Leaf weight="fill" color="var(--primary)" size={20} />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: "var(--muted-fg)" }}>Just diagnosed</div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>Powdery mildew</div>
                </div>
              </div>
              <div className="sev sev-moderate" style={{ marginTop: 10 }}><span style={{ width: "65%" }} /></div>
              <div style={{ fontSize: 11, color: "var(--muted-fg)", marginTop: 8 }}>Confidence 92% · 5 seconds ago</div>
            </div>
            <div className="card-soft" style={{ position: "absolute", top: 0, right: 30, padding: 16, transform: "rotate(4deg)", background: "var(--secondary)", color: "white" }}>
              <div style={{ fontSize: 11, opacity: 0.85 }}>This week</div>
              <div className="font-display" style={{ fontSize: 30, lineHeight: 1, marginTop: 4 }}>+ 23 leads</div>
              <div style={{ fontSize: 11, opacity: 0.85, marginTop: 4 }}>captured from chat</div>
            </div>
          </div>
        </div>
      </section>

      {/* Soft pill marquee — gentle */}
      <div style={{ background: "var(--primary)", color: "white", overflow: "hidden", padding: "14px 0" }}>
        <div className="soft-marquee" style={{ fontSize: 13, fontWeight: 600, letterSpacing: "0.06em" }}>
          {Array(2).fill(null).map((_, i) => (
            <React.Fragment key={i}>
              <span>🌿 Grown in McMinnville, TN</span>
              <span style={{ opacity: 0.5 }}>•</span>
              <span>Nursery Capital of the World</span>
              <span style={{ opacity: 0.5 }}>•</span>
              <span>5 Founding Nurseries · Spring 2026</span>
              <span style={{ opacity: 0.5 }}>•</span>
              <span>Hand-picked · by application</span>
              <span style={{ opacity: 0.5 }}>•</span>
              <span>Lifetime-locked founding pricing</span>
              <span style={{ opacity: 0.5 }}>•</span>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Pain points */}
      <section className="section">
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 24px" }}>
          <div style={{ textAlign: "center", maxWidth: 700, margin: "0 auto 56px" }}>
            <span className="overline">The everyday struggles</span>
            <h2 className="font-display" style={{ fontSize: "clamp(36px, 5vw, 56px)", marginTop: 14, letterSpacing: "-0.02em" }}>
              You already know the problems.
              <br />
              <span className="font-script" style={{ color: "var(--secondary)", fontSize: "0.9em" }}>Here's how NurserySense helps.</span>
            </h2>
          </div>
          <div className="problems-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
            {[
              { icon: Plant, color: "var(--primary)", soft: "var(--primary-soft)", n: "01", p: "Labor shortages", s: "65% of nurseries can't hire enough crew. Wages up, applicants down.", ai: "Photo-based scouting cuts 2–3 hrs of manual disease checks per crew, per week." },
              { icon: Leaf, color: "var(--secondary)", soft: "var(--secondary-soft)", n: "02", p: "Pest & disease loss", s: "By the time you spot it on the row, it's spread to 200 plants.", ai: "Snap a leaf — AI names the disease and treatment in 5 seconds." },
              { icon: Package, color: "#8B6E1A", soft: "var(--accent-soft)", n: "03", p: "Inventory shrinkage", s: "Plants die between count and ship. Surprise losses every quarter.", ai: "Live counts, loss reasons, and AI demand forecast for the next 30 days." },
              { icon: ChatCircleDots, color: "var(--primary)", soft: "var(--primary-soft)", n: "04", p: "After-hours leads", s: "Calls go to voicemail. Landscapers buy from the next nursery.", ai: "Friendly chatbot answers 24/7 and texts each lead straight to your phone." },
              { icon: Sparkle, color: "var(--secondary)", soft: "var(--secondary-soft)", n: "05", p: "Marketing time", s: "You're growing trees, not writing newsletters.", ai: "Coming soon — auto-generated weekly availability emails for landscapers.", soon: true },
              { icon: Package, color: "#8B6E1A", soft: "var(--accent-soft)", n: "06", p: "Wholesale quotes", s: "A landscaper calls for 50 redbuds and 100 boxwoods. You scribble it, lose the slip.", ai: "Coming soon — AI builds quote PDFs from your inventory in 10 seconds, emails them, tracks acceptance.", soon: true },
            ].map(({ icon: Icon, color, soft, ...c }) => (
              <div key={c.n} className="card card-hover" style={{ padding: 24 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 16, background: soft, display: "grid", placeItems: "center" }}>
                    <Icon size={22} weight="fill" color={color} />
                  </div>
                  <span style={{ fontFamily: "Caveat, cursive", fontSize: 24, color: "var(--muted-fg)" }}>no. {c.n}</span>
                </div>
                <h3 className="font-display" style={{ fontSize: 26, marginTop: 16 }}>{c.p}</h3>
                <p style={{ fontSize: 15, marginTop: 8, color: "var(--text-soft)", lineHeight: 1.55 }}>{c.s}</p>
                <div style={{ marginTop: 18, padding: "14px 16px", background: soft, borderRadius: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color }}>NurserySense fix</div>
                  <p style={{ fontSize: 14, marginTop: 6 }}>{c.ai}</p>
                  {c.soon && <span className="badge badge-warn" style={{ marginTop: 10 }}>Coming soon</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features showcase */}
      <section className="section" style={{ background: "var(--surface)", position: "relative" }}>
        <Sprig style={{ position: "absolute", top: 40, right: -30 }} size={180} rotate={-30} color="var(--leaf-pale)" />
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 24px" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <span className="overline">Three tools, one app</span>
            <h2 className="font-display" style={{ fontSize: "clamp(36px, 5vw, 56px)", marginTop: 12 }}>Built for boots, made on a phone.</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 28 }} className="hero-grid">
            <div className="frame" style={{ position: "relative", background: "var(--text)", color: "white", overflow: "hidden", borderRadius: "var(--radius-lg)" }}>
              <img src={LEAF_IMG} alt="Leaf disease" style={{ width: "100%", height: 320, objectFit: "cover" }} />
              <div style={{ padding: 32 }}>
                <span className="badge badge-soft" style={{ background: "rgba(255,255,255,0.12)", color: "white" }}>Feature 01</span>
                <h3 className="font-display" style={{ fontSize: 36, marginTop: 12, color: "white" }}>Plant Health Scanner</h3>
                <p style={{ marginTop: 10, fontSize: 16, color: "#cfd5cc", lineHeight: 1.55 }}>
                  Camera or upload. Returns disease, severity, treatment plan, and prevention. Works on any phone.
                </p>
                <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
                  <span className="badge" style={{ background: "rgba(255,255,255,0.15)", color: "white" }}>Powdery mildew</span>
                  <span className="badge badge-warn">Severity: moderate</span>
                  <span className="badge" style={{ background: "rgba(255,255,255,0.15)", color: "white" }}>Confidence 92%</span>
                </div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateRows: "1fr 1fr", gap: 28 }}>
              <div className="card-soft" style={{ padding: 32, background: "linear-gradient(135deg, var(--secondary) 0%, #B86A48 100%)", color: "white", borderRadius: "var(--radius-lg)" }}>
                <span className="badge" style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>Feature 02</span>
                <h3 className="font-display" style={{ fontSize: 30, marginTop: 12, color: "white" }}>Inventory + AI Forecast</h3>
                <p style={{ marginTop: 10, fontSize: 15, lineHeight: 1.55 }}>
                  Every variety, location, and dollar tracked. AI tells you what to reorder, hold, or mark down based on Tennessee growing seasons.
                </p>
                <div style={{ marginTop: 14, padding: "12px 14px", background: "rgba(255,255,255,0.18)", borderRadius: 12, fontSize: 13, fontWeight: 600 }}>
                  → Eastern Redbud · demand HIGH · reorder 80
                </div>
              </div>
              <div className="card-soft" style={{ padding: 32, background: "linear-gradient(135deg, var(--primary) 0%, #406048 100%)", color: "white", borderRadius: "var(--radius-lg)" }}>
                <span className="badge" style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>Feature 03</span>
                <h3 className="font-display" style={{ fontSize: 30, marginTop: 12, color: "white" }}>Customer Chatbot</h3>
                <p style={{ marginTop: 10, fontSize: 15, lineHeight: 1.55 }}>
                  Answers "what plant for shade and clay soil?" in plain English. Captures every name and number for your follow-up list.
                </p>
                <div style={{ marginTop: 14, padding: "12px 14px", background: "rgba(255,255,255,0.18)", borderRadius: 12, fontSize: 13, fontWeight: 600 }}>
                  → 23 leads captured this week
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial / image break */}
      <section style={{ background: "var(--bg-alt)", padding: "84px 0", position: "relative", overflow: "hidden" }}>
        <Sprig style={{ position: "absolute", top: -40, left: -40 }} size={280} rotate={70} color="var(--leaf-pale)" />
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 24px", display: "grid", gridTemplateColumns: "1fr 1.2fr", gap: 56, alignItems: "center", position: "relative" }} className="hero-grid">
          <div className="frame" style={{ borderRadius: "var(--radius-lg)" }}>
            <img src={FARMER_IMG} alt="Nursery worker" style={{ width: "100%", height: 480, objectFit: "cover", display: "block" }} />
          </div>
          <div>
            <span className="overline">Why local matters</span>
            <h2 className="font-display" style={{ fontSize: "clamp(36px, 5vw, 56px)", marginTop: 14 }}>
              Built next door, <br />
              <span className="font-script" style={{ color: "var(--secondary)" }}>not in Silicon Valley.</span>
            </h2>
            <p style={{ fontSize: 17, color: "var(--text-soft)", marginTop: 18, lineHeight: 1.6 }}>
              The big software companies don't know a redbud from a maple. NurserySense is built and supported here in McMinnville, trained on Tennessee plant species and the way wholesale crews actually work — clipboards, gloves, mud, and all.
            </p>
            <div style={{ marginTop: 32, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }} className="stats-grid">
              {[
                { v: "300+", l: "nurseries in Warren County" },
                { v: "8–14", l: "labor hrs saved / week" },
                { v: "$199", l: "/ month, locked-in for Founding Nurseries" },
              ].map((s) => (
                <div key={s.l}>
                  <div className="font-display" style={{ fontSize: 44, lineHeight: 1, color: "var(--primary)" }}>{s.v}</div>
                  <div style={{ fontSize: 13, color: "var(--muted-fg)", marginTop: 6 }}>{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="section">
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 24px" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <span className="overline">Plain pricing. No tricks.</span>
            <h2 className="font-display" style={{ fontSize: "clamp(36px, 5vw, 56px)", marginTop: 12 }}>Pick a plan after the field test.</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 24 }}>
            {[
              { p: "Starter", price: 199, who: "Family-run retail/wholesale, 1 location", incl: ["Plant scanner", "Inventory + shrinkage", "Customer chatbot", "1 staff seat"] },
              { p: "Pro", price: 399, who: "Up to 5 staff seats, priority support", incl: ["Everything in Starter", "AI demand forecast", "Lead routing to phone", "5 staff seats", "Chatbot embed on your site"], featured: true },
              { p: "Enterprise", price: 1299, who: "Multi-site wholesale + drone", incl: ["Everything in Pro", "Drone scan integration", "Custom AI training", "Dedicated success manager"] },
            ].map((t) => (
              <div key={t.p} className="card card-hover" style={{
                padding: 28,
                background: t.featured ? "linear-gradient(160deg, var(--primary), #3F5640)" : "var(--surface)",
                color: t.featured ? "white" : "inherit",
                border: t.featured ? "none" : "1px solid var(--border)",
                position: "relative",
              }}>
                {t.featured && <span className="badge" style={{ background: "var(--accent)", color: "var(--text)", position: "absolute", top: -12, left: 24 }}>Most popular</span>}
                <span className="overline" style={{ color: t.featured ? "var(--accent)" : "var(--secondary)" }}>{t.p}</span>
                <div className="font-display" style={{ fontSize: 56, marginTop: 10, lineHeight: 1 }}>${t.price}<span style={{ fontSize: 16, fontFamily: "Nunito", fontWeight: 600 }}>/mo</span></div>
                <p style={{ fontSize: 14, marginTop: 6, color: t.featured ? "#dceadc" : "var(--muted-fg)" }}>{t.who}</p>
                <ul style={{ marginTop: 22, paddingLeft: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 10, fontSize: 14 }}>
                  {t.incl.map((i) => (
                    <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                      <Leaf size={16} weight="fill" color={t.featured ? "var(--accent)" : "var(--primary)"} style={{ flexShrink: 0, marginTop: 2 }} />
                      <span>{i}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ position: "relative", padding: "96px 0", background: "linear-gradient(135deg, var(--secondary) 0%, #B26544 100%)", color: "white", overflow: "hidden" }}>
        <Sprig style={{ position: "absolute", top: -40, left: -20 }} size={240} rotate={20} color="rgba(255,255,255,0.15)" />
        <Sprig style={{ position: "absolute", bottom: -40, right: -20 }} size={260} rotate={200} color="rgba(255,255,255,0.12)" />
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 24px", textAlign: "center", position: "relative" }}>
          <span className="badge" style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>5 hand-picked nurseries · Spring 2026</span>
          <h2 className="font-display" style={{ fontSize: "clamp(40px, 6vw, 72px)", color: "white", marginTop: 16 }}>
            Apply to be a<br />
            <span className="font-script" style={{ fontSize: "0.85em" }}>Founding Nursery.</span>
          </h2>
          <div style={{ marginTop: 32, display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <Link to="/apply" className="btn" style={{ background: "white", color: "var(--text)", padding: "16px 32px", fontSize: 16 }} data-testid="footer-cta-register">
              Apply for a founding slot <ArrowRight size={18} weight="bold" />
            </Link>
            <Link to="/login" className="btn btn-ghost" style={{ color: "white", border: "1.5px solid rgba(255,255,255,0.4)", padding: "16px 32px", fontSize: 16 }} data-testid="footer-cta-demo">
              See the demo first
            </Link>
          </div>
        </div>
      </section>

      <footer style={{ background: "var(--text)", color: "rgba(255,255,255,0.7)", padding: "32px 24px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 14, fontSize: 13 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Leaf size={18} weight="fill" color="var(--accent)" />
            <span>© 2026 NurserySense · McMinnville, TN · Made with red clay and care.</span>
          </div>
          <div style={{ display: "flex", gap: 18, alignItems: "center" }}>
            <Link to="/blog" style={{ color: "rgba(255,255,255,0.85)", textDecoration: "underline" }}>Field Notes</Link>
            <Link to="/login" style={{ color: "rgba(255,255,255,0.85)", textDecoration: "underline" }}>Sign in</Link>
            <span style={{ fontFamily: "Caveat, cursive", fontSize: 18, color: "var(--accent)" }}>grow with us</span>
          </div>
        </div>
      </footer>

      <style>{`
        .hero-grid { grid-template-columns: 1.05fr 1fr; }
        @media (max-width: 1100px) {
          .problems-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 700px) {
          .problems-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 900px) {
          .hero-grid { grid-template-columns: 1fr !important; }
          .stats-grid { grid-template-columns: repeat(3, 1fr) !important; }
        }
      `}</style>
    </div>
  );
}
